import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { cn } from "../lib/cn";

/**
 * IconButton — icon-only inline interactive control.
 *
 * Use for toolbars, kebab menus, close/back affordances inside modals,
 * password-eye toggles inside inputs — anywhere a `<Button>` would feel
 * heavy because the action is a small icon-only nudge.
 *
 * Shape (canonical, fixed across the platform):
 * - 40×40 hit target, 16px glyph, fully-rounded.
 * - No border or fill at rest. Hover renders a faint accent tint and
 *   shifts text to `--accent-foreground`.
 * - Round, NOT notched: the Onyx hex notch is for rectangular surfaces; a
 *   round icon control keeps its circle. It still shares the offset-key
 *   language via the press — a springy 1px press-DOWN on `:active` (a CSS
 *   translate + overshoot ease, the same bounce every Button/KeycapButton
 *   uses), so the whole lib presses consistently.
 * - Required `aria-label` — TS-enforced + dev-only runtime warning if
 *   missing or empty.
 * - `motion={false}` opts out of the press feedback (static).
 * - `asChild` composes onto a router Link or other custom element.
 *
 * Why a fixed size: every IconButton across the platform is the same
 * size so users can predict the hit target and so designers don't have
 * to pick. If a single call site genuinely needs a different size (a
 * very rare escape hatch), pass `className="h-X w-X"` and accept the
 * inconsistency at that one site.
 *
 * Composition: pass any `lucide-react` (or other) icon as the single
 * child. The base classes size the icon via `[&_svg]:size-4`.
 */
export const iconButtonVariants = cva(
  [
    "inline-flex select-none items-center justify-center rounded-full",
    "h-10 w-10 [&_svg]:size-4",
    "ring-offset-background",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "hover:bg-accent/10 hover:text-accent-foreground",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0",
  ].join(" "),
);

/** The shared offset-key press: a springy 1px press-DOWN with overshoot ease. */
const ICON_PRESS = "transition-transform duration-150 ease-[cubic-bezier(0.34,1.45,0.6,1)] active:translate-y-px";

type IconButtonElementProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "onAnimationStart" | "onDragStart" | "onDragEnd" | "onDrag"
>;

export interface IconButtonProps extends IconButtonElementProps {
  /** Screen-reader label. Required because the button has no visible text. */
  "aria-label": string;
  /** Render children via Radix Slot (e.g. wrap a router Link or anchor). */
  asChild?: boolean;
  /** Disable framer-motion press-feedback. Default true. */
  motion?: boolean;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      className,
      asChild = false,
      motion: enableMotion = true,
      "aria-label": ariaLabel,
      children,
      ...rest
    },
    ref,
  ) => {
    if (process.env.NODE_ENV !== "production" && (!ariaLabel || ariaLabel.trim() === "")) {
      // eslint-disable-next-line no-console
      console.warn(
        "IconButton: missing or empty `aria-label`. Icon-only controls need a descriptive label for screen readers.",
      );
    }

    // The press feedback is the shared CSS offset-key bounce (gated by `motion`);
    // it rides on the host element so `asChild` consumers get it too.
    const classes = cn(iconButtonVariants(), enableMotion && ICON_PRESS, className);

    if (asChild) {
      return (
        <Slot
          ref={ref}
          className={classes}
          aria-label={ariaLabel}
          {...(rest as React.HTMLAttributes<HTMLElement>)}
        >
          {children}
        </Slot>
      );
    }

    return (
      <button
        ref={ref}
        type="button"
        className={classes}
        aria-label={ariaLabel}
        {...rest}
      >
        {children}
      </button>
    );
  },
);

IconButton.displayName = "IconButton";
